import { InMemoryCache } from '@apollo/client';
import { KeyArgsFunction, KeySpecifier } from '@apollo/client/cache/inmemory/policies';
import { relayStylePagination } from '@apollo/client/utilities';

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        tagsV2: baseCursorPagination(['query']),
        channelsV2: baseCursorPagination(['query', 'countryIds', 'numberTypes', 'limit', 'order', 'sort']),
        getAllNotificationV2: baseCursorPagination(),
        contactTags: relayStylePagination(['contactId']),
        contactNotes: relayStylePagination(['contactId']),
        contactNotesV2: baseCursorPagination(['contactId']),
        callerIdsV2: baseCursorPagination(['query']),
        workspaceMembersV2: baseCursorPagination(['query', 'status', 'sort', 'order', 'role', 'teams']),
        teamsV2: baseCursorPagination(['query', 'limit', 'order', 'sort']),
        contactsV2: baseCursorPagination([
          'query',
          'nameStartsWith',
          'countryCode',
          'sourceType',
          'tags',
          'sort',
          'fields',
          'excludeCountryCode',
        ]),
        cannedResponsesV2: baseCursorPagination(['query']),
        getBlockedContactsV2: baseCursorPagination(['query', 'order', 'sort']),
        commandResponses: baseCursorPagination(['query']),
        inProcessPhoneNumbersV2: baseCursorPagination(['query']),
        assignChannelsV2: baseCursorPagination(['limit', 'query', 'order', 'sort']),
        aiKnowledgeSources: baseCursorPagination(['query', 'limit', 'order', 'sort']),
        aiAgents: baseCursorPagination(['query', 'limit', 'order', 'sort']),
        aiAgentCallSessions: baseCursorPagination(['aiAgentId', 'includeTestCalls', 'limit']),
        purchaseFailedPhoneNumbers: baseCursorPagination(['query']),
        fetchJournalEntries: baseCursorPagination([
          'transactionDateFrom',
          'transactionDateTo',
          'credit',
          'usageType',
          'fields',
        ]),
        recentMessageConversationsV2: baseCursorPagination([
          'channelId',
          'query',
          'markAsClosed',
          'conversationStatus',
          'messageDirection',
          'voicemail',
          'scheduleMessage',
          'unread',
        ]),
        messageConversationsV2: baseCursorPagination(['channelId', 'clientId']),
        contactTagsV2: relayStylePagination(['contactId']),
        fetchAllCountries: baseCursorPagination(['query']),
        listPinnedMessageConversationsV2: baseCursorPagination(['channelId', 'clientId']),
        getChatMessagesV2: baseCursorPagination(['roomId']),
        invoicesV2: baseCursorPagination(['query', 'status', 'transactionDateFrom', 'transactionDateTo']),
        listNumberSubscriptionDetailsForDate: baseCursorPagination(['nextBillingDate']),
        channelConversations: baseCursorPagination(['clientId', 'query', 'countryId', 'sort']),
        channelsForNewConversations: baseCursorPagination(['clientId', 'query', 'countryId']),
        activeWorkspacesV2: baseCursorPagination(['query', 'limit']),
        integrationChannelsV2: baseCursorPagination(['integrationUserId', 'query', 'countryCode']),
        integrationConfiguration: relayStylePagination(['id', 'integrationConfigurationId']),
        integrationsV2: baseCursorPagination(['includeInactive', 'query']),
        attributeValuesV2: baseCursorPagination(['attributeName', 'query']),
        availablePhoneNumbersV2: baseCursorPagination([
          'countryCode',
          'numberType',
          'smsEnabled',
          'voiceEnabled',
          'mmsEnabled',
          'faxEnabled',
          'excludeAllAddressRequired',
          'excludeLocalAddressRequired',
          'excludeForeignAddressRequired',
          'beta',
          'contains',
          'areaCode',
          'inLocality',
          'limit',
        ]),
        areaCodes: baseCursorPagination(['query', 'order', 'limit']),
        messageSchedules: baseCursorPagination(['channelId', 'clientId']),
        getCallSettings: baseCursorPagination(['query', 'order', 'limit', 'sort']),
        getChannelGroupsInCallSetting: baseCursorPagination([
          'callSettingId',
          'query',
          'order',
          'limit',
          'sort',
          'onlyAssignedGroups',
        ]),
        contactComplianceConsents: baseCursorPagination(['query', 'campaigns']),
        channelGroups: baseCursorPagination(['query', 'order', 'limit', 'sort']),
        getChannelByGroup: baseCursorPagination(['groupId', 'query']),
        getWorkspaceChannelMembers: baseCursorPagination(['channelId', 'query']),
        listBulkMessagingCampaign: baseCursorPagination([
          'query',
          'limit',
          'order',
          'sort',
          'status',
          'campaignType',
          'createdBy',
          'initiatedFrom',
          'initiatedTo',
        ]),
        listBulkMessagingCampaignRuns: baseCursorPagination([
          'query',
          'limit',
          'order',
          'status',
          'sort',
          'campaignId',
        ]),
        getRecipientsByCampaignRun: baseCursorPagination([
          'query',
          'limit',
          'order',
          'status',
          'sort',
          'campaignRunId',
        ]),
        fetchBulkMessagingCampaignRecipients: baseCursorPagination([
          'query',
          'limit',
          'order',
          'status',
          'sort',
          'campaignId',
        ]),
        getPackages: baseCursorPagination(['type', 'query', 'sortBy', 'countryIds']),
        workspacePackages: baseCursorPagination(['status', 'isActive', 'query', 'sort', 'order']),
        getChannelOverview: relayStylePagination(['channelId']),
        getContactByPhoneNumber: relayStylePagination(['phoneNumber']),
        liveConferences: relayStylePagination(['query', 'channelId']),
        aiDashboardMemberPerformanceV2: baseCursorPagination(['sentimentType', 'fromDate', 'toDate', 'order', 'sort']),
        aiAdvanceCallReportV2: baseCursorPagination([
          'fromDate',
          'toDate',
          'sentimentType',
          'reasonType',
          'conclusionType',
          'callType',
          'callResolution',
          'userWorkspaceId',
          'order',
          'sort',
          'query',
        ]),
        bulkImportJobRows: baseCursorPagination(['query', 'limit', 'sort', 'jobId', 'order', 'status']),
      },
    },
    Recording: {
      keyFields: ['id'],
      fields: {
        // the subscription payload for MessageConversation doesn't select this field (the WS
        // gateway's schema lags the HTTP one), so a live-appended message would otherwise leave
        // this field "missing" and silently mark the whole messages list incomplete
        transcription: {
          read(existing) {
            return existing ?? null;
          },
        },
      },
    },
    UserWalletUsageDetails: {
      keyFields: ['id'],
    },
    RecentMessageConversation: {
      // to update fragment recent message after sending message
      keyFields: ['client', ['id'], 'channel', ['id']],
    },
    IntegrationChannelV2: {
      keyFields: ['id', 'integrationChannels', ['integrationUserId']],
    },
    IntegrationConfiguration: {
      keyFields: ['id', 'integrationUserId'],
    },
    TwilioPhoneNumber: {
      keyFields: ['phoneNumber', 'friendlyName'],
    },
    AiAdvanceCallReportV2: {
      keyFields: ['transcriptionId'],
    },
    CallSettingsType: {
      fields: {
        channelGroups: {
          merge(existing, incoming) {
            return incoming ?? existing;
          },
        },
      },
    },
  },
});

function baseCursorPagination(keyArgs?: KeySpecifier | KeyArgsFunction | undefined) {
  const defaultData = { data: [] };

  return {
    keyArgs,
    read(existing: typeof defaultData) {
      return existing;
    },
    merge(existing = defaultData, incoming = defaultData) {
      // prepending should be handled by sorting the list
      const combined = [...existing.data, ...incoming.data];
      const uniqueById = new Map();

      combined.forEach((item: { __ref: string }) => {
        if (item && item.__ref != null) {
          uniqueById.set(item.__ref, item); // override repeating data
        }
      });

      return {
        ...incoming,
        data: Array.from(uniqueById.values()),
      };
    },
  };
}
